/* eslint-disable react/no-danger */

import { get } from 'js-cookie';
import * as React from 'react';

import { Button, Icon } from '@ao-internal/components-react';
import * as digitalData from '@ao-internal/digital-data';

import { BASKET_COOKIE_NAME, TaggingEventNames } from '../../globals';
import { logError } from '../../helpers/logging';
import useChat from '../../hooks/useChat';
import BasketService from '../../services/BasketService';
import {
	ChatMessageTypes,
	ChatMessageTypesClassMap,
	InputTypes,
} from '../../store';
import LoadingSpinnerSimple from '../LoadingSpinnerSimple';
import { ProductSummaryModel } from '../ProductSummary';

export interface AddToBasketMessageContent {
	basketHandlerUrl: string;
	productSummary: ProductSummaryModel;
	introText: string;
	altPath?: {
		displayText: string;
		replyText?: string;
	};
	enableAltPath?: boolean;
}

interface AddToBasketMessageProps {
	groupIntent?: string;
	content: AddToBasketMessageContent;
	isReadOnly?: boolean;
}

interface BasketSimple {
	Products: { Count: number; Sku: string }[];
	TotalValue: number;
	ItemCount: number;
	BasketCountDescription: string;
	TotalValueDescription: string;
	CommaSeparatedListOfProductSkus: string;
}

enum ProductBasketStatus {
	NotInBasket,
	Pending,
	AddedToBasket,
	Error,
}

function getProductBasketStatus(sku: string) {
	const basketSimpleCookie = get(BASKET_COOKIE_NAME);

	if (!basketSimpleCookie) {
		return ProductBasketStatus.NotInBasket;
	}

	const basketSimpleData: BasketSimple = JSON.parse(
		decodeURIComponent(basketSimpleCookie)
	);

	return basketSimpleData.Products.find((product) => sku === product.Sku)
		? ProductBasketStatus.AddedToBasket
		: ProductBasketStatus.NotInBasket;
}

export const dataTestId = `${
	ChatMessageTypesClassMap[ChatMessageTypes.AddToBasket]
}-message`;

let addToBasketServiceInstance: BasketService;

export const AddToBasketMessage: React.FC<AddToBasketMessageProps> = ({
	groupIntent,
	content,
	isReadOnly,
}) => {
	const {
		basketHandlerUrl,
		productSummary,
		introText,
		altPath,
		enableAltPath = true,
	} = content;

	const altPathDisplayText = altPath ? altPath.displayText : undefined;
	const altPathReplyText =
		altPath && altPath.replyText ? altPath.replyText : altPathDisplayText;

	const { sendMessage } = useChat();

	const [productBasketStatus, setProductBasketStatus] = React.useState(
		getProductBasketStatus(productSummary.productCode)
	);

	async function addToBasketHandler() {
		if (!addToBasketServiceInstance) {
			addToBasketServiceInstance = new BasketService();
		}

		setProductBasketStatus(ProductBasketStatus.Pending);

		try {
			const basketResult = await addToBasketServiceInstance.addToBasket(
				basketHandlerUrl
			);

			digitalData.sendBeacon(TaggingEventNames.addToBasket, {
				productCode: productSummary.productCode,
				price: productSummary.price,
				brand: productSummary.brand,
				category: productSummary.category,
				fittingName: productSummary.fittingName,
			});

			if (basketResult && basketResult.TotalNumberOfItems) {
				addToBasketServiceInstance.updateHeaderBasketCount(
					basketResult.TotalNumberOfItems
				);
			}
		} catch (error) {
			logError(error, { basketHandlerUrl });
		}

		// TODO: Move this into the "try" once we have a sad path UX plan
		setProductBasketStatus(ProductBasketStatus.AddedToBasket);
	}

	function altPathHandler() {
		if (altPathReplyText) {
			digitalData.sendBeacon(TaggingEventNames.interaction, {
				type: 'click',
				element: 'button-message',
				value: altPathReplyText,
			});

			sendMessage(altPathReplyText, {
				groupIntent,
				inputType: InputTypes.Interaction,
			});
		}
	}

	return (
		<div
			ref={(node) => node && isReadOnly && node.setAttribute('inert', '')}
			data-testid={dataTestId}
			className="mx-2 mb-2"
		>
			<div className="flex flex-col items-start mb-2">
				<div className="max-w-85pc">
					<div className="border-gray-40 text-body-sm self-start bg-white border border-solid rounded-md rounded-tl-none">
						<div className="px-3 py-2 break-words whitespace-pre-line">
							{introText}
						</div>
					</div>
				</div>
			</div>
			<div className="flex flex-wrap -m-1">
				<div className="flex w-1/2 px-1 py-1">
					<Button
						data-testid="chatbot-add-to-basket"
						variant="primary"
						disabled={
							productBasketStatus === ProductBasketStatus.AddedToBasket
								? true
								: false
						}
						className="inline-flex items-center justify-center w-full"
						onClick={
							productBasketStatus === ProductBasketStatus.NotInBasket
								? addToBasketHandler
								: undefined
						}
					>
						{productBasketStatus === ProductBasketStatus.NotInBasket && (
							<React.Fragment>
								<Icon
									icon="basket"
									className="inline-flex mr-1 text-lg leading-none"
									style={{ fontSize: '20px' }}
								/>
								Add to basket
							</React.Fragment>
						)}
						{productBasketStatus === ProductBasketStatus.Pending && (
							<LoadingSpinnerSimple size={20} />
						)}
						{productBasketStatus === ProductBasketStatus.AddedToBasket && (
							<React.Fragment>
								<Icon
									icon="basket"
									className="inline-flex mr-1 text-lg leading-none"
									style={{ fontSize: '20px' }}
								/>
								Added to basket
							</React.Fragment>
						)}
					</Button>
				</div>
				{enableAltPath &&
					altPathDisplayText &&
					productBasketStatus === ProductBasketStatus.NotInBasket && (
						<div className="flex w-1/2 px-1 py-1">
							<Button
								data-testid="chatbot-add-to-basket-alt-path"
								variant="secondary"
								className="hover:bg-cta-actionhover hover:text-white justify-center w-full text-center bg-white"
								onClick={altPathHandler}
							>
								{altPathDisplayText}
							</Button>
						</div>
					)}
			</div>
		</div>
	);
};

export default AddToBasketMessage;
